import {
  KeyboardEvent,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Ellipsis, type LucideIcon } from "lucide-react";

// Deep imports on purpose: `ui/Table` imports this file, so `@/components/ui` (which re-exports Table) would loop.
import { Icon } from "@/components/ui/Icon";
import { IconButton, type ButtonSize, type ButtonVariant } from "@/components/ui/Button";
import { TONE_ROW } from "@/components/ui/StatusPill";

interface KebabMenuProps {
  /** Accessible label for the trigger (e.g. "Actions for dev"). */
  label: string;
  /** The trigger's look: `secondary` (bordered) next to a page's buttons, `ghost` inside a table row. */
  variant?: Extract<ButtonVariant, "secondary" | "ghost">;
  size?: ButtonSize;
  children: ReactNode;
}

const GAP = 4; // between the trigger and the menu
const MARGIN = 8; // kept clear of the window's edges

/**
 * Compact overflow menu (the ellipsis button). Keeps the primary action (Deploy) prominent
 * while folding secondary and destructive actions out of the default scan
 * path. Fully keyboard-operable: ArrowDown/Up + Home/End move between items,
 * Escape/Tab close and return focus to the trigger, outside click dismisses.
 *
 * The menu is drawn in a PORTAL, positioned from the trigger's rectangle. A row
 * menu lives inside a table inside a card that clips its overflow: an absolutely
 * positioned child would be cut off at the card's edge. It opens below the
 * trigger, right-aligned, and flips above when there is no room; it closes when
 * the page scrolls or resizes, since it would otherwise float away from its row.
 */
export function KebabMenu({ label, variant = "secondary", size = "md", children }: KebabMenuProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  function close(returnFocus = true) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  // Place the menu once it is in the DOM and measurable. Until then it is
  // rendered `invisible` (still laid out, so it can be measured, but not
  // painted): no flash at the wrong place.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const trigger = triggerRef.current?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (!trigger || !menu) return;
    const below = trigger.bottom + GAP;
    const fitsBelow = below + menu.height <= window.innerHeight - MARGIN;
    const top = fitsBelow ? below : Math.max(MARGIN, trigger.top - GAP - menu.height);
    const left = Math.min(
      Math.max(MARGIN, trigger.right - menu.width),
      window.innerWidth - menu.width - MARGIN,
    );
    setPos({ top, left });
  }, [open]);

  // Outside click dismisses (without yanking focus back to the trigger). The
  // menu is in a portal, so "inside" means the trigger OR the menu.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    // The menu closes BY ITSELF (scroll, resize, the trigger moved). If the keyboard
    // focus was on one of its entries, that entry is about to disappear and the focus
    // would fall on <body>: give it to the trigger — without scrolling, or the page
    // would jump back to a trigger the user had just scrolled away from. A click
    // outside is different: the focus goes where the user clicked.
    function onDetach() {
      const hadFocus = menuRef.current?.contains(document.activeElement);
      setOpen(false);
      if (hadFocus) triggerRef.current?.focus({ preventScroll: true });
    }
    document.addEventListener("mousedown", onDocClick);
    // `capture`: a scroll does not bubble, and the one that matters is usually
    // the page's scrolling container, not the window.
    window.addEventListener("scroll", onDetach, true);
    window.addEventListener("resize", onDetach);
    // The menu is placed once, on opening. A row that appears or disappears above
    // (a poll, a refetch) moves the trigger WITHOUT any scroll or resize, and the
    // menu would then float over the wrong row. So follow the trigger, and close
    // as soon as it is no longer where the menu was placed.
    const origin = triggerRef.current?.getBoundingClientRect();
    let frame = 0;
    function follow() {
      const now = triggerRef.current?.getBoundingClientRect();
      if (!now || !origin || Math.abs(now.top - origin.top) > 1 || Math.abs(now.left - origin.left) > 1) {
        onDetach();
        return;
      }
      frame = requestAnimationFrame(follow);
    }
    frame = requestAnimationFrame(follow);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("scroll", onDetach, true);
      window.removeEventListener("resize", onDetach);
      cancelAnimationFrame(frame);
    };
  }, [open]);

  // Move focus into the menu once it is placed — first enabled item. (An
  // `invisible` element cannot take focus, hence the wait for `pos`.)
  useEffect(() => {
    if (!open || !pos) return;
    const items = menuRef.current?.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([disabled])',
    );
    items?.[0]?.focus();
  }, [open, pos]);

  function enabledItems(): HTMLElement[] {
    return Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([disabled])',
      ) ?? [],
    );
  }

  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "Tab") {
      // Collapse the menu and put the focus back on the TRIGGER, without
      // cancelling the key: the browser then moves on from the trigger, to the
      // next (Tab) or previous (Shift+Tab) control of the row. The menu lives at
      // the very end of <body>, so leaving from where it is would jump out of the
      // page — to the browser's own UI, or to the last control of the document.
      close();
      return;
    }
    const items = enabledItems();
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length].focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length].focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1].focus();
    }
  }

  return (
    <>
      <IconButton
        ref={triggerRef}
        icon={Ellipsis}
        label={label}
        variant={variant}
        size={size}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      />
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={label}
            style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
            className={`fixed z-40 w-56 overflow-hidden rounded-md border border-line bg-surface py-1 shadow-lg ${
              pos ? "" : "invisible"
            }`}
            onKeyDown={onMenuKeyDown}
            // Picking an item closes the menu AND gives the focus back to the
            // trigger. An item that opens a confirmation matters most: the modal
            // remembers the focused element to return to when it closes, and the
            // item it would remember is about to disappear — the keyboard user
            // would land on <body>, far from the row they were working on.
            onClick={() => close()}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * A single menu row. ``danger`` tints it red for destructive actions: the
 * destructive gesture of a row LIVES here, and opens its confirmation.
 */
export function MenuItem({
  onClick,
  disabled,
  danger,
  icon,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  icon?: LucideIcon;
  title?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      disabled={disabled}
      title={title}
      onClick={onClick}
      // Inset ring: the menu is ``overflow-hidden`` and its items are full-bleed,
      // so a ring drawn outside the box would be clipped on both sides.
      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm focus-visible:outline-offset-[-2px] disabled:cursor-not-allowed disabled:opacity-40 ${
        danger
          ? TONE_ROW.danger
          : "text-ink hover:bg-sunken"
      }`}
    >
      {icon && <Icon icon={icon} />}
      {children}
    </button>
  );
}

/** Visual separator between groups (e.g. before the danger zone). */
export function MenuDivider({ label }: { label?: string }) {
  return (
    <div role="separator" className="my-1 border-t border-line px-3 pt-1">
      {label && (
        <span className="text-xs font-medium uppercase tracking-wider text-ink-muted">
          {label}
        </span>
      )}
    </div>
  );
}
